/** S11: enumerate signed reach, never a mutable members/holders registry. */
import type { DocHandle } from '@automerge/automerge-repo/slim'
import {
  Access,
  type Capability,
  docIdFromAutomergeUrl,
  GroupId,
  Identifier,
  type Keyhive,
} from '@automerge/automerge-repo-keyhive'
import { hex, unhex } from './invite'
import {
  canonicalBytes,
  isPersonDevice,
  KEY_ID,
  type Sharing,
  signBytes,
  verifyBytes,
} from './signatures'

export interface TargetPrincipal {
  id: string
  kind: 'group' | 'key'
  level: number
  /** Immediate signed parents, not names taken from another document. */
  via: string[]
  /** Keys that signed direct delegations to this principal. */
  issuers: string[]
}
export interface TargetMembership {
  principals: TargetPrincipal[]
  ownLevel: number
  ownerGroups: string[]
}
const documentId = (id: string) =>
  docIdFromAutomergeUrl(
    (id.startsWith('automerge:') ? id : `automerge:${id}`) as never,
  )

export async function readTargetMembership(
  hive: Keyhive,
  target: string,
): Promise<TargetMembership> {
  const document = documentId(target)
  const own = await hive.bestAccessForDoc(hive.id.toIdentifier(), document)
  if (!own?.isReader) throw new Error('No access to this target')
  const root = await hive.getDocument(document)
  if (!root) throw new Error('Target grants are unavailable')
  const found = new Map<string, TargetPrincipal>()
  const expanded = new Set<string>()
  const direct = await root.members()
  const pending: Array<{ via: string; grants: Capability[] }> = [
    { via: 'direct', grants: direct },
  ]
  while (pending.length) {
    const { via, grants } = pending.pop()!
    for (const grant of grants) {
      const id = hex(grant.who.id.toBytes())
      // Re-check effective reach. A path may be capped, revoked, or Relay-only.
      const access = await hive.bestAccessForDoc(grant.who.id, document)
      if (!access?.isReader) continue
      const row = found.get(id) ?? {
        id,
        kind: grant.who.isGroup ? ('group' as const) : ('key' as const),
        level: access.level,
        via: [],
        issuers: [],
      }
      if (!row.via.includes(via)) row.via.push(via)
      const issuer = hex(grant.proof.verifyingKey)
      if (!row.issuers.includes(issuer)) row.issuers.push(issuer)
      found.set(id, row)
      if (grant.who.isGroup && !expanded.has(id)) {
        expanded.add(id)
        const group = await hive.getGroup(new GroupId(unhex(id)))
        if (!group) throw new Error('Membership is still syncing')
        pending.push({ via: id, grants: await group.members() })
      }
    }
  }
  return {
    principals: [...found.values()].sort((a, b) => a.id.localeCompare(b.id)),
    ownLevel: own.level,
    ownerGroups: direct
      .filter((grant) => grant.who.isGroup && grant.can.equals(Access.admin()))
      .map((grant) => hex(grant.who.id.toBytes())),
  }
}

/** Who a key in a target's membership belongs to, each answer from a signature. */
export interface TargetKeyBindings {
  /** A key that is an admin member of a named person: one of their devices. */
  devices: Record<string, string>
  /** An Edit key whose grant a device of the hinted person signed: their agent. */
  agents: Record<string, string>
  /** Named people whose devices signed a direct grant to this key. */
  grantedBy: Record<string, string[]>
}

/** Bind the keys of a membership to the people the viewer can already name.
 * `people` must come from signed or own sources (the viewer's own person, the
 * people an invite key admitted). `agentHints` may come from editable session
 * metadata: a hint names an agent only when a device of that person signed the
 * key's grant, so a planted hint names nobody. Nothing here removes a row.
 */
export async function bindTargetKeys(
  sharing: Sharing,
  membership: TargetMembership,
  people: readonly string[],
  agentHints: Readonly<Record<string, string>> = {},
): Promise<TargetKeyBindings> {
  const bindings: TargetKeyBindings = { devices: {}, agents: {}, grantedBy: {} }
  const named = people.filter((id) => KEY_ID.test(id))
  const deviceOf = async (person: string, key: string) => {
    try {
      return await isPersonDevice(sharing, person, key)
    } catch {
      return false
    }
  }
  for (const row of membership.principals) {
    if (row.kind !== 'key') continue
    for (const person of named) {
      if (row.via.includes(person) && (await deviceOf(person, row.id))) {
        bindings.devices[row.id] = person
        break
      }
    }
    if (bindings.devices[row.id]) continue
    const granted: string[] = []
    for (const person of named)
      for (const issuer of row.issuers)
        if (!granted.includes(person) && (await deviceOf(person, issuer)))
          granted.push(person)
    if (granted.length) bindings.grantedBy[row.id] = granted
    const hinted = agentHints[row.id]
    if (!hinted || !KEY_ID.test(hinted) || row.level !== 2) continue
    for (const issuer of row.issuers)
      if (await deviceOf(hinted, issuer)) {
        bindings.agents[row.id] = hinted
        break
      }
  }
  return bindings
}

/** A short-lived, target-bound statement. Never persisted in a shared log. */
export interface TargetPresence {
  domain: 'mod:sharing:presence:v1'
  target: string
  person: string
  signer: string
  name: string
  /** Present only for a key working as an agent in this session. */
  session?: string
  at: number
  signature: string
}
export async function signTargetPresence(
  sharing: Sharing,
  target: string,
  person: string,
  name: string,
  at = Date.now(),
  session?: string,
): Promise<TargetPresence> {
  const value = {
    domain: 'mod:sharing:presence:v1' as const,
    target,
    person,
    signer: hex(sharing.hive.keyhive.id.toBytes()),
    name: name.slice(0, 100),
    at,
    ...(session ? { session } : {}),
  }
  return {
    ...value,
    signature: await signBytes(sharing, canonicalBytes(value)),
  }
}
export async function verifyTargetPresence(
  sharing: Sharing,
  target: string,
  raw: unknown,
  now = Date.now(),
): Promise<TargetPresence | undefined> {
  if (!raw || typeof raw !== 'object') return
  const p = raw as TargetPresence
  if (
    p.domain !== 'mod:sharing:presence:v1' ||
    p.target !== target ||
    !KEY_ID.test(p.person) ||
    !KEY_ID.test(p.signer) ||
    typeof p.name !== 'string' ||
    p.name.length > 100 ||
    !Number.isFinite(p.at) ||
    p.at > now + 5000 ||
    now - p.at > 30000
  )
    return
  const { signature, ...value } = p
  if (!(await verifyBytes(p.signer, signature, canonicalBytes(value)))) return
  if (p.session !== undefined) {
    if (typeof p.session !== 'string') return
    const membership = await readTargetMembership(
      sharing.hive.keyhive,
      p.session,
    )
    const agent = membership.principals.find(
      (row) => row.id === p.signer && row.kind === 'key',
    )
    if (!agent || agent.level !== 2) return
    let delegated = false
    for (const issuer of agent.issuers) {
      if (await isPersonDevice(sharing, p.person, issuer)) delegated = true
    }
    if (!delegated) return
  } else if (!(await isPersonDevice(sharing, p.person, p.signer))) return
  const hive = sharing.hive.keyhive
  const doc = documentId(target)
  for (const id of [p.person, p.signer]) {
    if (
      !(await hive.bestAccessForDoc(new Identifier(unhex(id)), doc))?.atLeast(
        Access.read(),
      )
    )
      return
  }
  return p
}

/** A tab's native publication stops at its worker unless that worker publishes
 * upstream. Preserve the end-to-end signed statement, verify reach, and relay
 * each signer's newest statement once so echoes cannot create a loop.
 * Attach only to already-held documents; this never opens a document.
 */
export function forwardTargetPresence(
  sharing: Sharing,
  handle: DocHandle<unknown>,
) {
  const seen = new Map<string, number>()
  let stopped = false
  const receive = ({ message }: { message: unknown }) => {
    void (async () => {
      const p = await verifyTargetPresence(sharing, handle.documentId, message)
      if (!p || stopped) return
      const now = Date.now()
      for (const [signer, at] of seen) if (now - at > 30000) seen.delete(signer)
      if ((seen.get(p.signer) ?? -Infinity) >= p.at) return
      seen.set(p.signer, p.at)
      handle.broadcast(p)
    })().catch(() => {})
  }
  handle.on('ephemeral-message', receive)
  return () => {
    stopped = true
    seen.clear()
    handle.off('ephemeral-message', receive)
  }
}
